import { useState, useEffect, useRef, useCallback } from 'react';
import { store } from '../store';

const DRAFT_PREFIX = 'draft_';
const DEFAULT_DEBOUNCE_MS = 500;
const DEFAULT_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

interface DraftEntry {
  value: string;
  savedAt: number;
}

interface UseDraftAutosaveOptions {
  draftKey: string;
  debounceMs?: number;
  expiryMs?: number;
}

interface UseDraftAutosaveReturn {
  initialValue: string;
  saveDraft: (value: string) => void;
  clearDraft: () => void;
  hasDraft: boolean;
}

function getUserId(): string | null {
  const user = store.getState().auth.user;
  return user?.id != null ? String(user.id) : null;
}

function buildStorageKey(userId: string, draftKey: string): string {
  return `${DRAFT_PREFIX}${userId}_${draftKey}`;
}

function readDraft(storageKey: string, expiryMs: number): string | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;

    const entry: DraftEntry = JSON.parse(raw);
    if (Date.now() - entry.savedAt > expiryMs) {
      localStorage.removeItem(storageKey);
      return null;
    }
    return entry.value || null;
  } catch {
    localStorage.removeItem(storageKey);
    return null;
  }
}

function writeDraft(storageKey: string, value: string): void {
  if (!value.trim()) {
    localStorage.removeItem(storageKey);
    return;
  }
  const entry: DraftEntry = { value, savedAt: Date.now() };
  localStorage.setItem(storageKey, JSON.stringify(entry));
}

/**
 * Hook that auto-saves user input text to localStorage with debouncing.
 * Restores draft on mount, clears on send. Scoped per user ID.
 */
export function useDraftAutosave(options: UseDraftAutosaveOptions): UseDraftAutosaveReturn {
  const { draftKey, debounceMs = DEFAULT_DEBOUNCE_MS, expiryMs = DEFAULT_EXPIRY_MS } = options;
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const storageKeyRef = useRef<string | null>(null);

  // Compute storage key
  const userId = getUserId();
  const currentStorageKey = userId ? buildStorageKey(userId, draftKey) : null;
  storageKeyRef.current = currentStorageKey;

  // Read initial value (only on mount or when draftKey changes)
  const [initialValue, setInitialValue] = useState<string>(() => {
    if (!currentStorageKey) return '';
    return readDraft(currentStorageKey, expiryMs) ?? '';
  });

  const [hasDraft, setHasDraft] = useState<boolean>(() => {
    if (!currentStorageKey) return false;
    return readDraft(currentStorageKey, expiryMs) != null;
  });

  // Re-read when draftKey changes (e.g. conversation switch, session ID loaded)
  useEffect(() => {
    if (!currentStorageKey) {
      setInitialValue('');
      setHasDraft(false);
      return;
    }
    const restored = readDraft(currentStorageKey, expiryMs);
    setInitialValue(restored ?? '');
    setHasDraft(restored != null);
  }, [currentStorageKey, expiryMs]);

  const saveDraft = useCallback((value: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      const key = storageKeyRef.current;
      if (key) {
        writeDraft(key, value);
      }
    }, debounceMs);
  }, [debounceMs]);

  const clearDraft = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    const key = storageKeyRef.current;
    if (key) {
      localStorage.removeItem(key);
    }
    setHasDraft(false);
  }, []);

  // Cleanup pending debounce on unmount (do NOT clear draft itself)
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        // Flush the pending save before unmount
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return { initialValue, saveDraft, clearDraft, hasDraft };
}

/**
 * Get all draft localStorage keys for a given user.
 */
export function getAllDraftKeys(userId: string | number): string[] {
  const prefix = `${DRAFT_PREFIX}${userId}_`;
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) {
      keys.push(key);
    }
  }
  return keys;
}

/**
 * Clear all drafts for a given user.
 */
export function clearAllUserDrafts(userId: string | number): void {
  const keys = getAllDraftKeys(userId);
  keys.forEach(key => localStorage.removeItem(key));
}

/**
 * Clear expired drafts for a given user.
 */
export function clearExpiredDrafts(userId: string | number, expiryMs: number = DEFAULT_EXPIRY_MS): void {
  const keys = getAllDraftKeys(userId);
  const now = Date.now();
  keys.forEach(key => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return;
      const entry: DraftEntry = JSON.parse(raw);
      if (now - entry.savedAt > expiryMs) {
        localStorage.removeItem(key);
      }
    } catch {
      localStorage.removeItem(key);
    }
  });
}
